import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, StatusBadge, CRIT_TONE, OP_TONE, OP_LABEL, Label2 } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/components/ui/select";
import { Plus, CaretRight, CaretDown, Stack } from "@phosphor-icons/react";
import { toast } from "sonner";

const groupTypes = (types) => types.reduce((m, t) => { const k = t.kategorie || "Sonstiges"; (m[k] = m[k] || []).push(t); return m; }, {});
const EMPTY = { name: "", kundenbezeichnung: "", asset_type_key: "", customer_id: "", site_id: "", parent_id: "none", manufacturer: "", serial: "", create_default_components: true };

function CreateAssetDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [customers, setCustomers] = useState([]);
  const [types, setTypes] = useState([]);
  const [sites, setSites] = useState([]);
  const [siteAssets, setSiteAssets] = useState([]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY); setSites([]); setSiteAssets([]);
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/v1/catalogs/asset-types").then((r) => setTypes(r.data));
  }, [open]);

  // Kaskade: Kunde -> nur dessen Standorte
  const onCustomer = async (cid) => {
    setForm((f) => ({ ...f, customer_id: cid, site_id: "", parent_id: "none" }));
    setSiteAssets([]);
    const { data } = await api.get(`/sites?customer_id=${cid}`);
    setSites(data);
  };
  // Kaskade: Standort -> nur dessen Anlagen (für übergeordnete Anlage)
  const onSite = async (sid) => {
    setForm((f) => ({ ...f, site_id: sid, parent_id: "none" }));
    const { data } = await api.get(`/assets?site_id=${sid}`);
    setSiteAssets(data);
  };

  const submit = async () => {
    const at = types.find((t) => t.key === form.asset_type_key);
    const cust = customers.find((c) => c.id === form.customer_id);
    if (!form.name || !at || !form.customer_id || !form.site_id) return;
    try {
      const { data } = await api.post("/assets", {
        name: form.name, asset_type: at.name, asset_type_key: at.key,
        kundenbezeichnung: form.kundenbezeichnung || null,
        customer_id: form.customer_id, customer: cust?.name || null, site_id: form.site_id,
        parent_id: form.parent_id === "none" ? null : form.parent_id,
        manufacturer: form.manufacturer || null, serial: form.serial || null,
        create_default_components: form.create_default_components,
      });
      toast.success("Anlage angelegt", { description: (data.components_created || []).length ? `${data.components_created.length} Komponenten erzeugt` : form.name });
      setOpen(false); onCreated();
    } catch (e) { toast.error("Fehler", { description: formatApiError(e.response?.data?.detail) }); }
  };

  const typeGroups = groupTypes(types);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-asset-button" className="gap-2"><Plus size={18} weight="bold" /> Neue Anlage</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="asset-dialog">
        <DialogHeader><DialogTitle className="font-heading">Neue Anlage anlegen</DialogTitle></DialogHeader>
        <DialogDescription>Kunde → Standort → (übergeordnete Anlage) → Anlagentyp. Auswahllisten filtern sich gegenseitig.</DialogDescription>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2"><Label2>Bezeichnung *</Label2>
            <Input data-testid="asset-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="z. B. Verladestelle 01" /></div>
          <div className="space-y-1.5 sm:col-span-2"><Label2>Kundenbezeichnung / Interne Anlagenbezeichnung</Label2>
            <Input data-testid="asset-kundenbez" value={form.kundenbezeichnung} onChange={(e) => set("kundenbezeichnung", e.target.value)} placeholder="z. B. Tor 07 · Halle B Tor 2 · T-17" /></div>

          <div className="space-y-1.5"><Label2>Kunde *</Label2>
            <Select value={form.customer_id} onValueChange={onCustomer}>
              <SelectTrigger data-testid="asset-customer"><SelectValue placeholder="Kunde wählen" /></SelectTrigger>
              <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select></div>

          <div className="space-y-1.5"><Label2>Standort *</Label2>
            <Select value={form.site_id} onValueChange={onSite} disabled={!form.customer_id}>
              <SelectTrigger data-testid="asset-site"><SelectValue placeholder={form.customer_id ? (sites.length ? "Standort wählen" : "Kein Standort") : "Erst Kunde wählen"} /></SelectTrigger>
              <SelectContent>{sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select></div>

          <div className="space-y-1.5"><Label2>Übergeordnete Anlage</Label2>
            <Select value={form.parent_id} onValueChange={(v) => set("parent_id", v)} disabled={!form.site_id}>
              <SelectTrigger data-testid="asset-parent"><SelectValue placeholder="Keine (Hauptanlage)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Keine (eigenständige Anlage) —</SelectItem>
                {siteAssets.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select></div>

          <div className="space-y-1.5"><Label2>Anlagentyp *</Label2>
            <Select value={form.asset_type_key} onValueChange={(v) => set("asset_type_key", v)}>
              <SelectTrigger data-testid="asset-type"><SelectValue placeholder="Typ wählen" /></SelectTrigger>
              <SelectContent>
                {Object.entries(typeGroups).map(([kat, list]) => (
                  <SelectGroup key={kat}><SelectLabel>{kat}</SelectLabel>
                    {list.map((t) => <SelectItem key={t.key} value={t.key}>{t.name}</SelectItem>)}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select></div>

          <div className="space-y-1.5"><Label2>Hersteller</Label2>
            <Input value={form.manufacturer} onChange={(e) => set("manufacturer", e.target.value)} /></div>
          <div className="space-y-1.5"><Label2>Seriennummer</Label2>
            <Input value={form.serial} onChange={(e) => set("serial", e.target.value)} /></div>

          <label className="flex items-center gap-2 text-sm sm:col-span-2">
            <Checkbox data-testid="asset-default-comps" checked={form.create_default_components} onCheckedChange={(v) => set("create_default_components", !!v)} />
            Standard-Komponenten des Typs automatisch anlegen
          </label>
        </div>
        <DialogFooter>
          <Button data-testid="asset-save" onClick={submit} disabled={!form.name || !form.asset_type_key || !form.customer_id || !form.site_id}>Anlegen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function flatten(nodes, expanded, depth, acc) {
  nodes.forEach((n) => {
    acc.push({ ...n, depth });
    if (expanded[n.id] && n.children.length) flatten(n.children, expanded, depth + 1, acc);
  });
  return acc;
}

export default function Assets() {
  const [assets, setAssets] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  const load = useCallback(() => { api.get("/assets").then((r) => setAssets(r.data)); }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  if (!assets) return <div className="space-y-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-md" />)}</div>;

  const map = {};
  assets.forEach((a) => (map[a.id] = { ...a, children: [] }));
  const roots = [];
  assets.forEach((a) => {
    if (a.parent_id && map[a.parent_id]) map[a.parent_id].children.push(map[a.id]);
    else roots.push(map[a.id]);
  });
  const filteredRoots = q
    ? roots.filter((r) => r.name.toLowerCase().includes(q.toLowerCase()) || (r.customer || "").toLowerCase().includes(q.toLowerCase()))
    : roots;
  const rows = flatten(filteredRoots, expanded, 0, []);

  return (
    <div>
      <PageHeader title="Anlagen" subtitle={`${assets.length} Anlagen · Digital Twin & Anlagenlebenslauf`}>
        <CreateAssetDialog onCreated={load} />
      </PageHeader>

      <div className="mb-4 flex items-center gap-3">
        <Input data-testid="asset-search" placeholder="Anlage oder Kunde suchen…" value={q}
          onChange={(e) => setQ(e.target.value)} className="max-w-sm" />
      </div>

      <Card className="overflow-hidden rounded-md border">
        <div className="flex items-center gap-3 border-b border-border bg-muted/50 px-4 py-2.5">
          <Label2>Anlagenstruktur (hierarchisch)</Label2>
        </div>
        {rows.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">Keine Anlagen gefunden.</div>}
        <div className="divide-y divide-border">
          {rows.map((node) => (
            <div key={node.id} data-testid="asset-row" onClick={() => navigate(`/anlagen/${node.id}`)}
              className="flex cursor-pointer items-center gap-3 px-4 py-3 transition-colors hover:bg-accent">
              <div style={{ width: node.depth * 20 }} />
              <button onClick={(e) => { e.stopPropagation(); toggle(node.id); }}
                className={`flex h-5 w-5 items-center justify-center rounded ${node.children.length ? "text-muted-foreground hover:text-foreground" : "opacity-0"}`}>
                {expanded[node.id] ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
              </button>
              <Stack size={18} weight="duotone" className="shrink-0 text-primary" />
              <div className="min-w-0 flex-1">
                <div className="truncate font-semibold">{node.name}</div>
                <div className="truncate text-xs text-muted-foreground">{node.asset_type} · {node.customer || "—"}</div>
              </div>
              <span className="hidden font-mono text-xs text-muted-foreground sm:inline">{node.qr_code}</span>
              <StatusBadge tone={OP_TONE[node.operational_status]}>{OP_LABEL[node.operational_status]}</StatusBadge>
              <StatusBadge tone={CRIT_TONE[node.criticality]}>{node.criticality}</StatusBadge>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
